import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import { Print, ArrowBack, PictureAsPdf } from "@mui/icons-material";
import sailingService from "../../services/sailingService";

const AdminManifestPrint = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [html, setHtml] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    useEffect(() => {
        sailingService
            .getManifest(id)
            .then(({ data }) => setHtml(data.data.html))
            .catch((err) => setError(err.response?.data?.message || "Failed to load manifest"))
            .finally(() => setLoading(false));
    }, [id]);

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const response = await sailingService.getManifestPdf(id);
            const disposition = response.headers?.["content-disposition"] || "";
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            const filename = filenameMatch ? filenameMatch[1] : "Manifest.pdf";

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setError("Failed to download manifest PDF");
        } finally {
            setDownloadingPdf(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box className="no-print" sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button startIcon={<ArrowBack />} variant="outlined" size="small" onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Button startIcon={<Print />} variant="contained" size="small" onClick={() => window.print()} disabled={!html}>
                    Print
                </Button>
                <Button
                    startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadPdf}
                    disabled={!html || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {error && (
                <Alert severity="error" className="no-print" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Always white page / dark text — this is a document meant to be
                printed on paper, never a dark-mode surface, and the
                server-generated HTML assumes a white backdrop of its own. */}
            {html && (
                <Box
                    className="manifest-print-root"
                    sx={{ maxWidth: 950, mx: "auto", bgcolor: "#fff", color: "#0E1420", p: 2 }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            )}

            <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          @page { size: landscape; margin: 10mm; }

          /* The cover sheet and cargo table both hardcode max-width:900px
             (screen sizing) — that's wider than a portrait page and was
             clipping the right-most columns (CBF, Carrier Wt) off the
             printed page. Landscape + letting every element size to the
             actual (now-wider) print page fixes it without needing to
             touch the admin-editable template HTML itself. */
          .manifest-print-root, .manifest-print-root * {
            max-width: 100% !important;
          }
          .manifest-print-root table {
            width: 100% !important;
            font-size: 9px !important;
            table-layout: auto;
          }
          .manifest-print-root th,
          .manifest-print-root td {
            padding: 3px 4px !important;
            word-break: break-word;
          }
        }
      `}</style>
        </Box>
    );
};

export default AdminManifestPrint;
